
import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, filter, switchMap } from 'rxjs/operators'
import { uploadFile } from 'services/compose'
import { StoreThunkDispatch } from 'store/state'

export interface UploadProps {
  file: File
  chunkSize?: number // bytes
  onError: (error: Error) => void
  onProgress: (progress: number) => void
  onSuccess: (fileUrl: string, response: any) => void
  onUploadStarted?: (upload: { abort: (terminate?: boolean) => void }) => void
}

export function Upload(props: UploadProps) {
  const { onSuccess, onError } = props
  const dispatch = useDispatch<StoreThunkDispatch>()
  const upload$ = useRef<Subject<File | null>>(undefined)

  const onProgress = (e: ProgressEvent) => {
    const value = (e.loaded / e.total) * 100 // eslint-disable-line no-magic-numbers
    props.onProgress(value)
  }

  useEffect(() => {
    upload$.current = new Subject()
    const subscription = upload$.current.pipe(
      switchMap((file: File | null) => {
        // EXPL: null is passed when we want to cancel the currently running request
        // without causing the Observable stream to complete.
        if (file === null) {
          return observableOf(null)
        }

        const progress$ = { next: onProgress }
        if (props.onUploadStarted) {
          props.onUploadStarted({
            abort: (terminate?: boolean) => {
              if (terminate) {
                upload$.current?.next(null)
              } else {
                upload$.current?.unsubscribe()
              }
            }
          })
        }
        return dispatch(uploadFile(file, progress$)).pipe(catchError(error => observableOf({ error })))
      }),
      filter(response => Boolean(response)))
      .subscribe((response: any) => {
        if (response.error) {
          onError(response.error)
        } else {
          onSuccess(response.url, response)
        }
      })

    return () => {
      subscription.unsubscribe()
    }
    // EXPL: the upload subject is (re)created only when the result handlers change. `onProgress`
    // (recreated each render) and `props.onUploadStarted` are invoked mid-stream — listing them
    // would tear down and recreate the subject on every render, aborting the in-flight upload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, onSuccess, onError])

  useEffect(() => {
    upload$.current?.next(props.file)
  }, [props.file])

  return null
}

export default Upload
